import { useCallback, useEffect, useState } from 'react'
import {
  attendanceCreateEventType,
  attendanceDeleteEventType,
  attendanceLoadEventTypes,
  attendanceUpdateEventType,
  type AttendanceEventType,
} from '../../lib/apiPhpAdapter'
import './AttendanceOrgAdmin.css'
import './AttendanceOrgAdmin.css'
import './AttendanceEventTypesPanel.css'

type Props = {
  showAlert: (msg: string, type?: 'success' | 'error' | 'warning' | 'info') => void
  embedded?: boolean
  onChanged?: () => void | Promise<void>
}

type FormState = {
  code: string
  name: string
  kind: 'in' | 'out'
  color: string
  sort_order: string
  active: boolean
  counts_as_work: boolean
}

const emptyForm = (): FormState => ({
  code: '',
  name: '',
  kind: 'in',
  color: '',
  sort_order: '100',
  active: true,
  counts_as_work: true,
})

function formFromItem(it: AttendanceEventType): FormState {
  return {
    code: it.code || '',
    name: it.name || '',
    kind: it.kind === 'out' ? 'out' : 'in',
    color: it.color || '',
    sort_order: String(it.sort_order ?? 100),
    active: it.active === undefined || it.active === null ? true : Boolean(Number(it.active)),
    counts_as_work:
      it.counts_as_work === undefined || it.counts_as_work === null
        ? true
        : Boolean(Number(it.counts_as_work)),
  }
}

export function AttendanceEventTypesPanel({ showAlert, embedded = false, onChanged }: Props) {
  const [items, setItems] = useState<AttendanceEventType[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [createForm, setCreateForm] = useState<FormState>(emptyForm)
  const [editItem, setEditItem] = useState<AttendanceEventType | null>(null)
  const [editForm, setEditForm] = useState<FormState>(emptyForm)
  const [deleteItem, setDeleteItem] = useState<AttendanceEventType | null>(null)

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      const list = await attendanceLoadEventTypes()
      setItems(Array.isArray(list) ? list : [])
    } catch (e: unknown) {
      showAlert(e instanceof Error ? e.message : 'Nepodařilo se načíst události', 'error')
    } finally {
      setLoading(false)
    }
  }, [showAlert])

  useEffect(() => {
    void reload()
  }, [reload])

  useEffect(() => {
    if (!createOpen && !editItem && !deleteItem) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) {
        setCreateOpen(false)
        setEditItem(null)
        setDeleteItem(null)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [createOpen, editItem, deleteItem, busy])

  const openCreate = () => {
    setEditItem(null)
    setDeleteItem(null)
    setCreateForm(emptyForm())
    setCreateOpen(true)
  }

  const openEdit = (it: AttendanceEventType) => {
    setCreateOpen(false)
    setDeleteItem(null)
    setEditItem(it)
    setEditForm(formFromItem(it))
  }

  const openDelete = (it: AttendanceEventType) => {
    setCreateOpen(false)
    setEditItem(null)
    setDeleteItem(it)
  }

  const onCreate = async () => {
    const name = createForm.name.trim()
    if (!name) {
      showAlert('Název je povinný', 'warning')
      return
    }
    setBusy(true)
    try {
      await attendanceCreateEventType({
        name,
        code: createForm.code.trim() || undefined,
        kind: createForm.kind,
        color: createForm.color.trim() || null,
        sort_order: parseInt(createForm.sort_order, 10) || 100,
        active: createForm.active,
        counts_as_work: createForm.counts_as_work,
      })
      showAlert('Událost vytvořena', 'success')
      setCreateForm(emptyForm())
      setCreateOpen(false)
      await reload()
      await onChanged?.()
    } catch (e: unknown) {
      showAlert(e instanceof Error ? e.message : 'Uložení selhalo', 'error')
    } finally {
      setBusy(false)
    }
  }

  const onSaveEdit = async () => {
    if (!editItem) return
    const name = editForm.name.trim()
    if (!name) {
      showAlert('Název je povinný', 'warning')
      return
    }
    setBusy(true)
    try {
      await attendanceUpdateEventType(editItem.id, {
        name,
        code: editForm.code.trim() || editItem.code,
        kind: editForm.kind,
        color: editForm.color.trim() || null,
        sort_order: parseInt(editForm.sort_order, 10) || 100,
        active: editForm.active,
        counts_as_work: editForm.counts_as_work,
      })
      showAlert('Událost upravena', 'success')
      setEditItem(null)
      await reload()
      await onChanged?.()
    } catch (e: unknown) {
      showAlert(e instanceof Error ? e.message : 'Uložení selhalo', 'error')
    } finally {
      setBusy(false)
    }
  }

  const onConfirmDelete = async () => {
    if (!deleteItem) return
    setBusy(true)
    try {
      await attendanceDeleteEventType(deleteItem.id)
      showAlert('Smazáno', 'success')
      setDeleteItem(null)
      await reload()
      await onChanged?.()
    } catch (e: unknown) {
      showAlert(e instanceof Error ? e.message : 'Smazání selhalo', 'error')
    } finally {
      setBusy(false)
    }
  }

  const renderFields = (form: FormState, setForm: (fn: (f: FormState) => FormState) => void, opts?: { showActive?: boolean }) => (
    <div className="att-abs-modal__fields">
      <label>
        Kód
        <input
          value={form.code}
          disabled={busy}
          placeholder="např. arrival"
          onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
        />
      </label>
      <label>
        Název (text tlačítka)
        <input
          value={form.name}
          disabled={busy}
          autoFocus={!opts?.showActive}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
        />
      </label>
      <label>
        Směr
        <select
          value={form.kind}
          disabled={busy}
          onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value === 'out' ? 'out' : 'in' }))}
        >
          <option value="in">Příchod / vstup (in)</option>
          <option value="out">Odchod / výstup (out)</option>
        </select>
      </label>
      <label>
        Barva (volitelně, #hex)
        <input
          value={form.color}
          disabled={busy}
          placeholder="#0d9488"
          onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
        />
      </label>
      <label>
        Pořadí
        <input
          type="number"
          value={form.sort_order}
          disabled={busy}
          onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))}
        />
      </label>
      {opts?.showActive ? (
        <label className="att-abs-modal__check">
          <input
            type="checkbox"
            checked={form.active}
            disabled={busy}
            onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
          />
          aktivní (zobrazit tlačítko uživateli)
        </label>
      ) : (
        <label className="att-abs-modal__check">
          <input
            type="checkbox"
            checked={form.active}
            disabled={busy}
            onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
          />
          aktivní
        </label>
      )}
      <label className="att-abs-modal__check">
        <input
          type="checkbox"
          checked={form.counts_as_work}
          disabled={busy}
          onChange={(e) => setForm((f) => ({ ...f, counts_as_work: e.target.checked }))}
        />
        Započítat do odpracované doby
      </label>
      <p className="att-evt-modal__hint">
        Vypnuto = interval mezi odchodem a příchodem této události se do odpracované doby nepočítá
        (typicky lékař). Zapnuto = jen značka, čas se počítá (např. nepřímá činnost). U Příchod /
        Odchod nemá vliv.
      </p>
    </div>
  )

  const Wrapper = embedded ? 'div' : 'section'
  const wrapperClass = embedded ? 'att-cat' : 'attendance-panel att-cat'

  return (
    <Wrapper className={wrapperClass}>
      {!embedded ? <h2>Události</h2> : null}
      <p className="att-cat__intro">
        Typy událostí pro uživatelský panel. Aktivní = tlačítka; u každé nastavíte, zda čas patří do
        odpracované doby.
      </p>

      <div className="att-org__add att-cat__add">
        <button type="button" className="system-button" disabled={busy} onClick={openCreate}>
          Přidat událost
        </button>
      </div>

      {loading ? <p className={embedded ? 'att-prehled__muted' : 'attendance-muted'}>Načítání…</p> : null}

      <div className="att-cat__wrap">
        <table className="att-cat__table att-cat__table--events">
          <thead>
            <tr>
              <th className="att-cat__col-code">Kód</th>
              <th className="att-cat__col-name">Název</th>
              <th className="att-cat__col-narrow">Směr</th>
              <th className="att-cat__col-narrow">Odprac.</th>
              <th className="att-cat__col-narrow">Pořadí</th>
              <th className="att-cat__col-narrow">Stav</th>
              <th className="att-cat__col-actions att-cat__col-fill" aria-label="Akce" />
            </tr>
          </thead>
          <tbody>
            {items.map((it) => {
              const isActive = it.active === undefined || it.active === null ? true : Boolean(Number(it.active))
              const countsWork =
                it.counts_as_work === undefined || it.counts_as_work === null
                  ? true
                  : Boolean(Number(it.counts_as_work))
              return (
                <tr key={it.id} className={isActive ? undefined : 'att-cat__row--inactive'}>
                  <td className="att-cat__col-code" title={it.code}>
                    {it.code}
                  </td>
                  <td className="att-cat__col-name" title={it.name}>
                    {it.color ? (
                      <span className="att-evt__name">
                        <span
                          className="att-evt__swatch"
                          aria-hidden
                          style={{ background: it.color }}
                        />
                        <span className="att-evt__name-text">{it.name}</span>
                      </span>
                    ) : (
                      it.name
                    )}
                  </td>
                  <td className="att-cat__col-narrow">{it.kind === 'out' ? 'odchod' : 'příchod'}</td>
                  <td className="att-cat__col-narrow">{countsWork ? 'ano' : 'ne'}</td>
                  <td className="att-cat__col-narrow">{it.sort_order}</td>
                  <td className="att-cat__col-narrow">{isActive ? 'aktivní' : 'neaktivní'}</td>
                  <td className="att-cat__col-actions">
                    <button
                      type="button"
                      className="system-button att-cat__btn"
                      disabled={busy}
                      onClick={() => openEdit(it)}
                    >
                      Upravit
                    </button>
                    <button
                      type="button"
                      className="system-button att-cat__btn att-cat__btn--danger"
                      disabled={busy}
                      onClick={() => openDelete(it)}
                    >
                      Smazat
                    </button>
                  </td>
                </tr>
              )
            })}
            {!loading && items.length === 0 ? (
              <tr>
                <td colSpan={7} className="att-cat__empty">
                  Žádné události v katalogu.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {createOpen ? (
        <div
          className="att-abs-modal-overlay"
          role="presentation"
          onClick={() => {
            if (!busy) setCreateOpen(false)
          }}
        >
          <div
            className="att-abs-modal att-evt-modal"
            role="dialog"
            aria-modal
            aria-labelledby="att-evt-create-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="att-evt-create-title">Nová událost</h3>
            {renderFields(createForm, setCreateForm)}
            <div className="att-abs-modal__actions">
              <button
                type="button"
                className="system-button"
                disabled={busy || !createForm.name.trim()}
                onClick={() => void onCreate()}
              >
                {busy ? 'Ukládám…' : 'Vytvořit'}
              </button>
              <button
                type="button"
                className="system-button"
                style={{ background: '#64748b' }}
                disabled={busy}
                onClick={() => setCreateOpen(false)}
              >
                Zrušit
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {editItem ? (
        <div
          className="att-abs-modal-overlay"
          role="presentation"
          onClick={() => {
            if (!busy) setEditItem(null)
          }}
        >
          <div
            className="att-abs-modal att-evt-modal"
            role="dialog"
            aria-modal
            aria-labelledby="att-evt-edit-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="att-evt-edit-title">Upravit událost</h3>
            {renderFields(editForm, setEditForm, { showActive: true })}
            <div className="att-abs-modal__actions">
              <button
                type="button"
                className="system-button"
                disabled={busy || !editForm.name.trim()}
                onClick={() => void onSaveEdit()}
              >
                {busy ? 'Ukládám…' : 'Uložit'}
              </button>
              <button
                type="button"
                className="system-button"
                style={{ background: '#64748b' }}
                disabled={busy}
                onClick={() => setEditItem(null)}
              >
                Zrušit
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {deleteItem ? (
        <div
          className="att-abs-modal-overlay"
          role="presentation"
          onClick={() => {
            if (!busy) setDeleteItem(null)
          }}
        >
          <div
            className="att-abs-modal att-evt-modal"
            role="dialog"
            aria-modal
            aria-labelledby="att-evt-delete-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="att-evt-delete-title">Smazat událost</h3>
            <p>
              Opravdu smazat „{deleteItem.name}“ ({deleteItem.code})? Historické záznamy v docházce
              zůstanou, ale tlačítko zmizí.
            </p>
            <div className="att-abs-modal__actions">
              <button
                type="button"
                className="system-button"
                style={{ background: '#ef4444' }}
                disabled={busy}
                onClick={() => void onConfirmDelete()}
              >
                {busy ? 'Mazání…' : 'Smazat'}
              </button>
              <button
                type="button"
                className="system-button"
                style={{ background: '#64748b' }}
                disabled={busy}
                onClick={() => setDeleteItem(null)}
              >
                Zrušit
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </Wrapper>
  )
}
